import {
  afterRenderEffect,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  model,
  viewChild,
  viewChildren,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { ISegmentedControlOption } from './segmented-control.interface';

@Component({
  imports: [IconComponent, MatTooltipModule, NgxSkeletonLoaderComponent, TranslateModule],
  selector: 'app-segmented-control',
  templateUrl: './segmented-control.component.html',
})
export class SegmentedControlComponent<T> {
  public readonly options = input.required<ISegmentedControlOption<T>[]>();
  public readonly loading = input<boolean>(false);
  public readonly value = model.required<T>();

  public readonly selectedValue = linkedSignal(() => this.value());
  public readonly thumbSlideMs = 250;

  private readonly segments = viewChildren<ElementRef<HTMLButtonElement>>('segment');
  private readonly thumb = viewChild.required<ElementRef<HTMLElement>>('thumb');

  private thumbPositioned = false;
  private commitTimeout?: ReturnType<typeof setTimeout>;

  constructor() {
    // Segment widths change with translations, font loading and counts, none of which are signals.
    const resizeObserver = new ResizeObserver(() => this.positionThumb());

    inject(DestroyRef).onDestroy(() => {
      resizeObserver.disconnect();
      clearTimeout(this.commitTimeout);
    });

    afterRenderEffect(() => {
      resizeObserver.disconnect();
      this.segments().forEach((segment) => resizeObserver.observe(segment.nativeElement));
    });

    afterRenderEffect(() => {
      this.positionThumb();
    });

    // A value set from outside wins over a commit that is still waiting for the slide to end.
    effect(() => {
      this.value();
      clearTimeout(this.commitTimeout);
    });
  }

  // The thumb moves right away, but the value is committed only after it has finished sliding:
  // whatever the parent re-renders on change (tables, charts) would otherwise stutter the slide.
  public select(value: T): void {
    if (value === this.selectedValue()) {
      return;
    }

    this.selectedValue.set(value);
    clearTimeout(this.commitTimeout);

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.commitTimeout = setTimeout(() => this.value.set(value), reducedMotion ? 0 : this.thumbSlideMs);
  }

  private positionThumb(): void {
    const thumb = this.thumb().nativeElement;
    const selectedIndex = this.options().findIndex((option) => option.value === this.selectedValue());
    const segment = this.segments()[selectedIndex]?.nativeElement;

    if (this.loading() || !segment) {
      thumb.style.opacity = '0';
      this.thumbPositioned = false;

      return;
    }

    // The first placement must not slide in from the left edge.
    // Toggle only transition-property: resetting the `transition` shorthand would also wipe the bound inline duration.
    const skipTransition = !this.thumbPositioned;

    if (skipTransition) {
      thumb.style.transitionProperty = 'none';
    }

    thumb.style.opacity = '1';
    thumb.style.width = `${segment.offsetWidth}px`;
    thumb.style.transform = `translateX(${segment.offsetLeft}px)`;

    if (skipTransition) {
      void thumb.offsetWidth;
      thumb.style.transitionProperty = '';
      this.thumbPositioned = segment.offsetWidth > 0;
    }
  }
}
